import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import './DataTable.css';

/* ─────────────────────────────────────────────────────────
   Drag-and-drop header (unchanged)
   ───────────────────────────────────────────────────────── */
function DndHeader({ field, children, onReorder, disabled }) {
  const [side, setSide] = useState(null);
  const ref = useRef(null);

  const onDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', field);
  };

  const onDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setSide(e.clientX - r.left < r.width / 2 ? 'left' : 'right');
  };

  const onDragLeave = () => setSide(null);

  const onDrop = (e) => {
    e.preventDefault();
    const fromField = e.dataTransfer.getData('text/plain');
    if (fromField && fromField !== field) onReorder?.(fromField, field, side === 'right');
    setSide(null);
  };

  return (
    <div
      ref={ref}
      className="dnd-header"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <span className="dnd-title">{children}</span>
      <span
        className="dnd-handle"
        title="Drag to reorder"
        aria-label={`Drag column ${field}`}
        draggable
        onDragStart={onDragStart}
        onMouseDown={(e) => e.stopPropagation()}
      >
        ⋮⋮
      </span>
      {side && <span className={`dnd-drop-indicator ${side}`} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   DataTable with optional infinite scroll
   ───────────────────────────────────────────────────────── */
const DataTable = ({
  columns,
  rows,
  onRowClick,
  rowCount,
  page,
  pageSize,
  onPageChange,
  minHeight,
  renderHoverAdornment,
  hoverRowPredicate = () => true,
  enableHeaderDnD = false,
  onHeaderReorder,

  // Infinite scroll
  continuousScroll = false,
  loadMore,                 // server-paging callback
  hasMore = true,
  loadingMore = false,
  scrollThreshold = 300,
  scrollDebounceMs = 100,

  // Client “reveal as you scroll” (when you don’t provide loadMore)
  clientBatchSize = 25,

  // NEW – minimal knobs, sensible defaults
  showColumnMenus = false,
  allowHeaderSorting = true,

  ...rest
}) => {
  // Prepare final column definitions with minimal overrides
  const colsProcessed = useMemo(() => {
    return (columns || []).map((c) => {
      // Respect existing props where provided, only override when needed
      const base = {
        align: c.align ?? 'center',
        headerAlign: c.headerAlign ?? 'center',
        // Hide per-column menu unless explicitly enabled at the table level
        disableColumnMenu: showColumnMenus ? (c.disableColumnMenu ?? false) : true,
        // Keep header click sorting unless caller disabled or table toggle off
        sortable: allowHeaderSorting ? (c.sortable ?? true) : false,
        // If you centralize filters elsewhere, default filterable to false unless set
        filterable: c.filterable ?? false,
      };

      // Preserve existing renderHeader; wrap in DnD only when enabled
      if (enableHeaderDnD && c.field !== '_qi') {
        const original = c.renderHeader;
        return {
          ...c,
          ...base,
          renderHeader: (params) => (
            <DndHeader field={c.field} onReorder={onHeaderReorder} disabled={false}>
              {original ? original(params) : (c.headerName ?? c.field)}
            </DndHeader>
          ),
        };
      }

      return { ...c, ...base };
    });
  }, [columns, enableHeaderDnD, onHeaderReorder, showColumnMenus, allowHeaderSorting]);

  // Optional tiny quick-info column
  const colsWithQi = useMemo(() => {
    if (!renderHoverAdornment) return colsProcessed;

    const qiCol = {
      field: '_qi',
      headerName: '',
      width: 20,
      minWidth: 20,
      maxWidth: 20,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) =>
        hoverRowPredicate(params.row)
          ? <div className="hover-adornment">{renderHoverAdornment({ row: params.row })}</div>
          : null,
    };
    return [qiCol, ...colsProcessed];
  }, [colsProcessed, renderHoverAdornment, hoverRowPredicate]);

  const wrapRef = useRef(null);
  const scrollerRef = useRef(null);
  const rafTickRef = useRef(false);
  const lastFireRef = useRef(0);

  // Find virtual scroller node
  useEffect(() => {
    if (!continuousScroll) return;
    const root = wrapRef.current;
    if (!root) return;
    const scroller =
      root.querySelector('.MuiDataGrid-virtualScroller') ||
      root.querySelector('[class*="MuiDataGrid-virtualScroller"]') ||
      root.querySelector('[class*="VirtualScroller"]');
    scrollerRef.current = scroller || null;
  }, [continuousScroll, rows]);

  // Client “reveal” mode when no server loadMore is provided
  const clientReveal = continuousScroll && typeof loadMore !== 'function';
  const [visibleCount, setVisibleCount] = useState(
    clientReveal ? Math.min(clientBatchSize, rows.length) : rows.length
  );

  // Reset visible slice when rows change (filters, new search, etc.)
  useEffect(() => {
    setVisibleCount(clientReveal ? Math.min(clientBatchSize, rows.length) : rows.length);
  }, [rows, clientReveal, clientBatchSize]);

  // Attach scroll listener
  useEffect(() => {
    if (!continuousScroll) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onScroll = () => {
      if (rafTickRef.current) return;
      rafTickRef.current = true;
      requestAnimationFrame(() => {
        rafTickRef.current = false;

        const now = Date.now();
        if (now - lastFireRef.current < scrollDebounceMs) return;

        const { scrollTop, scrollHeight, clientHeight } = scroller;
        const nearBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;

        if (!nearBottom) return;
        lastFireRef.current = now;

        if (clientReveal) {
          // Just reveal more of what we already have in memory
          setVisibleCount((n) => Math.min(n + clientBatchSize, rows.length));
        } else {
          // Ask parent to fetch/append more
          if (hasMore && !loadingMore && typeof loadMore === 'function') {
            loadMore();
          }
        }
      });
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [
    continuousScroll,
    clientReveal,
    clientBatchSize,
    rows.length,
    hasMore,
    loadingMore,
    loadMore,
    scrollThreshold,
    scrollDebounceMs,
  ]);

  const resolvedHeight = minHeight ?? 400;

  // Disable pagination entirely when continuous
  const enablePagination = !continuousScroll && (rowCount != null || onPageChange != null);

  // Keep these arrays defined to avoid older MUI “.includes of undefined” bug
  const safePageSize = pageSize ?? 25;
  const pageSizeOptions = enablePagination ? [safePageSize] : [];

  // Final rows handed to the grid
  const displayRows = clientReveal ? rows.slice(0, visibleCount) : rows;

  return (
    <div
      ref={wrapRef}
      className="legacy-datatable-scope"
      style={{ minHeight: resolvedHeight, height: resolvedHeight, width: '100%' }}
    >
      <DataGrid
        /* spread user props first; we override below when needed */
        {...rest}

        rows={displayRows}
        columns={colsWithQi}

        /* Hide ALL footer UI in continuous mode */
        hideFooter={continuousScroll}
        hideFooterPagination={continuousScroll}
        hideFooterSelectedRowCount={continuousScroll}

        /* Don’t pass page/pageSize when continuous to avoid “20 of 40” */
        pagination={enablePagination}
        paginationMode={
          enablePagination ? (rowCount && onPageChange ? 'server' : 'client') : undefined
        }
        rowCount={enablePagination ? rowCount : undefined}
        page={enablePagination && page != null ? page - 1 : undefined}
        pageSize={enablePagination ? safePageSize : undefined}
        rowsPerPageOptions={pageSizeOptions}   // v5 safety
        pageSizeOptions={pageSizeOptions}      // v6 safety
        onPageChange={(newZeroBased) =>
          enablePagination && onPageChange && onPageChange(newZeroBased + 1)
        }

        /* Keep easy header click sort with an UNSORT stop */
        sortingOrder={['asc', 'desc', null]}

        onRowClick={onRowClick}

        sx={{
          ...(renderHoverAdornment ? {
            '& .MuiDataGrid-columnHeader[data-field="_qi"]': {
              maxWidth: 20, minWidth: 20, width: 20, p: 0,
            },
            '& .MuiDataGrid-cell[data-field="_qi"]': {
              maxWidth: 20, minWidth: 20, width: 20, p: 0, justifyContent: 'center',
            },
          } : {}),
          /* Belt-and-suspenders: visually hide menu icon if menus are off */
          ...(showColumnMenus ? {} : {
            '& .MuiDataGrid-menuIcon': { display: 'none' },
            '& .MuiDataGrid-columnHeader:has(.MuiDataGrid-menuIcon)': { paddingRight: 8 },
          }),
        }}
      />
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    headerName: PropTypes.string,
    width: PropTypes.number,
  })).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowClick: PropTypes.func,
  rowCount: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderHoverAdornment: PropTypes.func,
  hoverRowPredicate: PropTypes.func,
  enableHeaderDnD: PropTypes.bool,
  onHeaderReorder: PropTypes.func,

  continuousScroll: PropTypes.bool,
  loadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  scrollThreshold: PropTypes.number,
  scrollDebounceMs: PropTypes.number,
  clientBatchSize: PropTypes.number,

  // NEW
  showColumnMenus: PropTypes.bool,
  allowHeaderSorting: PropTypes.bool,
};

DataTable.defaultProps = {
  onRowClick: () => {},
  pageSize: 10,
  page: undefined,
  rowCount: undefined,
  minHeight: 400,
  onPageChange: undefined,
  renderHoverAdornment: undefined,
  hoverRowPredicate: () => true,
  enableHeaderDnD: false,
  onHeaderReorder: undefined,

  continuousScroll: false,
  loadMore: undefined,
  hasMore: true,
  loadingMore: false,
  scrollThreshold: 300,
  scrollDebounceMs: 100,
  clientBatchSize: 25,

  // NEW defaults – matches your request app-wide with no call-site edits
  showColumnMenus: true,
  allowHeaderSorting: true,
};

export default DataTable;
